/**
 * Automation change batches keyed by the owning record or object guid.
 */
export type AutomationEntities = Record<string, AutomationModel>

/**
 * Change-batch wrapper used by automation response payloads.
 */
export interface AutomationModel {
    /** Current automation entities keyed by automation guid. */
    record?: Record<string, ResponseAutomation>

    /** Newly added automation entities keyed by automation guid. */
    added?: Record<string, ResponseAutomation>

    /** Updated automation entities keyed by automation guid. */
    updated?: Record<string, ResponseAutomation>

    /** Removed automation guids included in the change batch. */
    deleted?: string[]
}

/**
 * Supported automation execution modes.
 */
export enum AutomationTypes {
    /** Runs automatically when its conditions are met. */
    AUTO = 'auto',

    /** Runs only after a user triggers it manually. */
    MANUAL = 'manual',
}

/**
 * Persisted backend automation shape.
 */
export interface ResponseAutomation {
    /** Automation display name. */
    name: string

    /** Backend execution mode for the automation. */
    automation_type: AutomationTypes | string

    /** Numeric active flag returned by the backend. */
    is_active?: number

    /** User guid of the last editor. */
    last_user_guid: string

    /** Backend timestamp of the last change. */
    last_timestamp: string

    /** Trigger conditions keyed by condition guid. */
    conditions: AutomationConditionEntities

    /** Actions keyed by action guid. */
    actions: AutomationActionEntities

    /** User guids explicitly assigned to the automation. */
    user_guids?: string[]

    /** Optional system note stored with the automation. */
    system_note?: string

    /** Number of recent execution failures. */
    fail_count?: number

    /** Numeric flag indicating whether the automation is global. */
    is_global?: number
}

/**
 * Automation-condition entities keyed by condition guid.
 */
export type AutomationConditionEntities = Record<string, AutomationCondition>

/**
 * One automation trigger condition.
 */
export interface AutomationCondition {
    /** Backend source-type code that the condition reads from. */
    source_type: string

    /** Serialized source-specific configuration. */
    source_details: string

    /** Condition comparison values. */
    values: string[]
}

/**
 * Automation-action entities keyed by action guid.
 */
export type AutomationActionEntities = Record<string, AutomationAction>

/**
 * One automation action entry.
 */
export interface AutomationAction {
    /** Backend action-type code for the action entry. */
    action_type_code: 'action_item'

    /** Configuration payload for the action. */
    action_config: AutomationActionConfig
}

/**
 * Action-specific configuration for `action_item` automations.
 */
export interface AutomationActionConfig {
    /** Target field guid affected by the action. */
    target_object_field_guid: string

    /** Serialized value written by the action. */
    value: string
}
